import { BASE_CURRENCY, type Filters } from './consts';

export interface Rental {
    id: string;
    message_id: string;
    title: string;
    property_type?: string;
    location?: string;
    bedrooms?: number;
    bathrooms?: number;
    price_amount?: number;
    price_currency?: string;
    rental_period?: string;
    price_per_month?: number;
    price_per_bedroom_per_month?: number;
    media_urls: string[];
    amenities: string[];
    contact_type?: string;
    contact_url?: string;
    contact_name?: string;
    raw_description?: string;
    posted_at_source?: string;
    source_url?: string;
    source_channel_name?: string;
    source_channel_id?: string;
    source: string;
    slug?: string;
    topic_id?: string;
}

export type RentalDoc = Omit<Rental, 'price_per_month' | 'price_per_bedroom_per_month' | 'amenities' | 'media_urls'> & {
    amenities?: string[] | null;
    media_urls?: string[] | null;
};

export function pricePerMonthInBaseCurrency(rental: Rental, exchangeRates: Record<string, number> = {}): number | null {
    if (!rental.price_per_month || !isFinite(rental.price_per_month)) return null;
    const currency = rental.price_currency || BASE_CURRENCY;
    const rate = currency === BASE_CURRENCY ? 1 : exchangeRates[currency];
    if (!rate || !isFinite(rate)) return null;
    return rental.price_per_month / rate;
}

export function sortRentals(rentals: Rental[], sort: Filters['sort'], exchangeRates: Record<string, number> = {}): Rental[] {
    return [...rentals].sort((a, b) => {
        if (sort === 'pricePerMonthAsc') {
            return (pricePerMonthInBaseCurrency(a, exchangeRates) ?? Infinity)
                - (pricePerMonthInBaseCurrency(b, exchangeRates) ?? Infinity);
        }

        if (sort === 'pricePerMonthDesc') {
            return (pricePerMonthInBaseCurrency(b, exchangeRates) ?? -Infinity)
                - (pricePerMonthInBaseCurrency(a, exchangeRates) ?? -Infinity);
        }

        return 0;
    });
}

export const docToRental = (doc: RentalDoc): Rental => ({
    ...doc,
    property_type: doc.property_type?.toLowerCase() === 'unknown' ? undefined : doc.property_type,
    amenities: doc.amenities || [],
    media_urls: doc.media_urls || [],
    price_per_month: pricePerMonth(doc.price_amount, doc.rental_period),
    price_per_bedroom_per_month: pricePerBedroomPerMonth(doc.price_amount, doc.rental_period, doc.bedrooms),
});

const pricePerMonth = (price?: number, rentalPeriod?: string) => {
    if (!price) return NaN;
    if (rentalPeriod === 'month') return price;
    if (rentalPeriod === 'week') return price * 52 / 12;
    if (rentalPeriod === 'day') return price * 365 / 12;
    if (rentalPeriod === 'night') return price * 365 / 12;
    if (rentalPeriod === 'year') return price / 12;
    return NaN;
};

const pricePerBedroomPerMonth = (price?: number, rentalPeriod?: string, bedrooms?: number) => {
    if (!bedrooms || bedrooms <= 0) return Infinity;
    return pricePerMonth(price, rentalPeriod) / bedrooms;
};

// Preserve hidden/unresolved likes and their order. When several old IDs resolve
// to one property, retain its most recently saved position.
export function canonicalLikedIds(ids: string[], aliases: ReadonlyMap<string, string>): string[] {
    const mapped = ids.map(id => aliases.get(id) || id);
    return [...new Set(mapped.reverse())].reverse();
}
